import { jsx } from 'react/jsx-runtime';
import { forwardRef } from 'react';
import { useInteractionHandler } from '../hooks/use-interaction-handler.js';
import { useMergeRefs } from '../hooks/use-merge-refs.js';
import { useValidatedContext } from '../hooks/use-validated-context.js';
import { accept } from '../utils/event.js';
import { isStandardShortcutKeyPressed } from '../utils/modifiers.js';
import { MenuInternalContext, useMenuItemSelection } from './menu-v2.hooks.js';

/**
 * MenuV2.ItemPrimitive
 *
 * A low-level menu item that provides menu behavior (click handling,
 * close-on-click, keyboard navigation) without any visual styling.
 * Use this when you need full control over the item's appearance.
 *
 * @compose { "parent": ["MenuV2.Root", "MenuV2.Group", "MenuV2.SubMenu"], "multi": true }
 */
const ItemPrimitive = forwardRef(({ children, disabled, onClick, onMiddleClick, closeOnClick = true, recordingKey, targetId, ...props }, refIn) => {
    const { onCloseRoot, isClickHold } = useValidatedContext(MenuInternalContext, 'MenuV2.ItemPrimitive', 'MenuV2.Root');
    const handleClick = useInteractionHandler((e) => {
        if (!disabled) {
            onClick?.(e);
            if (closeOnClick && (!isStandardShortcutKeyPressed(e) || isClickHold.current)) {
                onCloseRoot();
            }
        }
    }, { eventName: 'click', recordingKey }, [closeOnClick, disabled, onClick, recordingKey]);
    const { itemProps, targetRef } = useMenuItemSelection({ targetId });
    const ref = useMergeRefs(refIn, targetRef);
    const passMiddleClick = (e) => {
        if (e.button === 1) {
            onMiddleClick?.(e);
        }
    };
    const handlePointerUp = (e) => {
        if (e.pointerType !== 'mouse' || !isClickHold.current)
            return;
        if (e.button === 1) {
            onMiddleClick?.(e);
        }
        else if (e.button === 0) {
            handleClick(e);
        }
    };
    return (jsx("li", { ...props, ...itemProps, ref: ref, "aria-disabled": disabled, role: "menuitem", onClick: handleClick, onAuxClick: passMiddleClick, onPointerUp: handlePointerUp, onKeyDown: (e) => {
            if (e.key === ' ' || e.key === 'Enter') {
                handleClick(e);
                accept(e);
            }
        }, children: children }));
});
ItemPrimitive.displayName = 'MenuV2.ItemPrimitive';

export { ItemPrimitive };
//# sourceMappingURL=menu-v2.item-primitive.js.map
